import React, { useEffect, useMemo, useState } from 'react';
import { Button, Image, Space, Typography, message } from 'antd';
import { EyeOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { axiosInstance } from '../../config';
import { combineGenerationPrompt, reviewPromptValues } from './reviewPrompt';

const ROLE_LABELS = {
    system: 'Система',
    user: 'Пользователь',
    assistant: 'Ассистент',
    tool: 'Инструмент',
};

const apiError = (error, fallback) =>
    error.response?.data?.detail ||
    error.response?.data?.error?.message ||
    fallback;

const ReviewReplyGenerator = ({
    reviewId,
    review,
    generating = false,
    onGenerating,
    onGenerated,
}) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [promptOpen, setPromptOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const { data } = await axiosInstance.get('/v1/reviews/reply-templates');
                if (!cancelled) setTemplates(Array.isArray(data) ? data : []);
            } catch (error) {
                if (!cancelled) {
                    message.error(apiError(error, 'Не удалось загрузить шаблоны ответов'));
                    setTemplates([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const selected = useMemo(
        () => templates.find((item) => item.id === selectedId) || null,
        [templates, selectedId]
    );
    const values = useMemo(() => reviewPromptValues(review), [review]);
    const promptText = useMemo(() => {
        if (!selected) return '';
        return combineGenerationPrompt(
            selected.preview_prompt,
            selected.main_prompt,
            values
        );
    }, [selected, values]);
    const contextMessages = selected?.context_messages || [];
    const photoUrls = values.photo_urls || [];

    const selectTemplate = (templateId) => {
        setSelectedId(templateId);
        setPromptOpen(false);
    };

    const generate = async () => {
        if (!selectedId || !reviewId || generating) return;
        if (!selected?.model) {
            message.warning('В шаблоне не выбрана модель');
            return;
        }
        onGenerating?.(true);
        try {
            const { data } = await axiosInstance.post(
                `/v1/reviews/${encodeURIComponent(reviewId)}/generate-reply`,
                { template_id: selectedId },
                { timeout: 120000 }
            );
            const text = String(data?.text || '').trim();
            if (!text) {
                message.error('Модель вернула пустой ответ');
                return;
            }
            onGenerated?.(text, {
                cost: data?.cost,
                usage: data?.usage,
            });
            message.success('Ответ сгенерирован');
        } catch (error) {
            message.error(apiError(error, 'Не удалось сгенерировать ответ'));
        } finally {
            onGenerating?.(false);
        }
    };

    return (
        <div>
            <Typography.Text strong>Генерация ответа</Typography.Text>
            {loading ? (
                <div className="mt-2 text-sm text-black/45">Загружаем шаблоны…</div>
            ) : templates.length === 0 ? (
                <div className="mt-2 text-sm text-black/55">
                    Нет шаблонов ответов.{' '}
                    <Link to="/reviews/templates">Создайте их в разделе «Шаблоны ответов»</Link>
                    .
                </div>
            ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                    {templates.map((template) => (
                        <Button
                            key={template.id}
                            type={selectedId === template.id ? 'primary' : 'default'}
                            onClick={() => selectTemplate(template.id)}
                        >
                            {template.name}
                        </Button>
                    ))}
                </div>
            )}

            {selected ? (
                <div className="mt-3">
                    <Space wrap>
                        <Button
                            icon={<EyeOutlined />}
                            onClick={() => setPromptOpen((open) => !open)}
                        >
                            Просмотр промта
                        </Button>
                        <Button
                            type="primary"
                            icon={<ThunderboltOutlined />}
                            loading={generating}
                            onClick={generate}
                        >
                            Сгенерировать
                        </Button>
                    </Space>
                    {promptOpen ? (
                        <div className="mt-3 space-y-3 rounded-lg border border-black/10 bg-black/[0.02] p-3">
                            <div>
                                <Typography.Text type="secondary">Контекст</Typography.Text>
                                {contextMessages.length === 0 ? (
                                    <div className="mt-1 text-sm text-black/45">
                                        Контекст не задан
                                    </div>
                                ) : (
                                    <div className="mt-1 space-y-2">
                                        {contextMessages.map((item, index) => (
                                            <div key={`${item.role}-${index}`}>
                                                <div className="text-xs font-medium text-black/55">
                                                    {ROLE_LABELS[item.role] || item.role}
                                                </div>
                                                <div className="whitespace-pre-wrap text-sm">
                                                    {item.content}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div>
                                <Typography.Text type="secondary">Промт</Typography.Text>
                                <pre className="mt-1 mb-0 max-h-64 overflow-auto whitespace-pre-wrap break-words font-sans text-sm">
                                    {promptText || '—'}
                                </pre>
                            </div>
                            <div>
                                <Typography.Text type="secondary">
                                    Прикреплённые фото
                                </Typography.Text>
                                {photoUrls.length === 0 ? (
                                    <div className="mt-1 text-sm text-black/45">
                                        Нет фото для прикрепления
                                    </div>
                                ) : (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {photoUrls.map((url, index) => (
                                            <Image
                                                key={`${url}-${index}`}
                                                src={url}
                                                width={72}
                                                height={72}
                                                className="rounded object-cover"
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default ReviewReplyGenerator;
